import { Group, Mesh } from 'three/webgpu'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
import type { VesselConfig } from '../physics/Vessel.ts'

export type SmallCraftKind = 'police-boat' | 'fireboat' | 'sailboat' | 'zodiac' | 'landing-craft'
export function isSmallCraft(kind: string): kind is SmallCraftKind {
  return kind === 'police-boat' || kind === 'fireboat' || kind === 'sailboat' || kind === 'zodiac' || kind === 'landing-craft'
}

// Gentle acceleration and damped steering, with distinct cruising speeds.
export const SMALL_CRAFT_CONFIG: Record<SmallCraftKind, VesselConfig> = {
  zodiac: { name: 'Red Zodiac', mass: 16_000, length: 7, beam: 2.8, draft: .5,
    dragForward: 100, dragLateral: 2_000, dragYaw: 160_000, yawInertiaScale: 2,
    lateralResistanceOffset: .7, buoyancySamples: [6, 3], pitchDamping: 1, rollDamping: 1 },
  'police-boat': { name: 'Police patrol boat', mass: 90_000, length: 12, beam: 4.2, draft: .9,
    dragForward: 800, dragLateral: 8_000, dragYaw: 1.8e6, yawInertiaScale: 1.8,
    lateralResistanceOffset: 1.2, buoyancySamples: [6, 3], pitchDamping: 1, rollDamping: 1 },
  fireboat: { name: 'Fireboat', mass: 140_000, length: 16, beam: 5.2, draft: .9,
    dragForward: 1_000, dragLateral: 12_000, dragYaw: 3e6, yawInertiaScale: 1.8,
    lateralResistanceOffset: 1.4, buoyancySamples: [6, 3], pitchDamping: 1, rollDamping: 1 },
  sailboat: { name: 'Sailing yacht', mass: 30_000, length: 11, beam: 3.4, draft: 2.05,
    dragForward: 700, dragLateral: 6_000, dragYaw: 7e5, yawInertiaScale: 1.8,
    lateralResistanceOffset: 1, buoyancySamples: [6, 3], pitchDamping: 1, rollDamping: 1 },
  // A blunt, flat-bottomed box: slow, steady, and happy to sit on a beach.
  'landing-craft': { name: 'Landing craft', mass: 60_000, length: 11, beam: 3.3, draft: .9,
    dragForward: 1_000, dragLateral: 6_000, dragYaw: 1.1e6, yawInertiaScale: 1.8,
    lateralResistanceOffset: 1.1, buoyancySamples: [6, 3], pitchDamping: 1, rollDamping: 1 },
}
export const SMALL_CRAFT_PROPULSION = {
  // Base propulsion; Tug applies the fleet-wide increase through lower drag.
  // Arcade RIB: quick acceleration, fastest in the fleet, with damped helm.
  zodiac: { ahead: 170_000, astern: 12_000, rudder: .2, lift: 100 },
  // Quadratic drag requires four times the thrust for twice the top speed.
  'police-boat': { ahead: 560_000, astern: 65_000, rudder: .24, lift: 800 },
  // Match the patrol boat's thrust-to-drag ratio for the same top speed.
  fireboat: { ahead: 700_000, astern: 55_000, rudder: .24, lift: 1_200 },
  // Auxiliary power uses the same accessible controls; sails are visual.
  sailboat: { ahead: 18_000, astern: 9_000, rudder: .24, lift: 350 },
  // Low top speed, but a strong astern pull to back off the sand. The wide
  // rudder angle keeps the slow boat answering the helm.
  'landing-craft': { ahead: 90_000, astern: 45_000, rudder: .5, lift: 2_500 },
}

/** Metres, Y-up, +Z bow; each call owns its disposable GPU resources. */
export async function loadSmallCraftModel(kind: SmallCraftKind): Promise<Group> {
  const { scene } = await new GLTFLoader().loadAsync(`/models/playable-boats/${kind}.glb`)
  const vessel = new Group()
  vessel.name = `${kind}-blender`
  scene.rotation.y = -Math.PI / 2
  vessel.add(scene)
  vessel.traverse(object => {
    if (!(object as Mesh).isMesh) return
    object.castShadow = true
    object.receiveShadow = true
  })
  return vessel
}
